import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import useGenerateInvoice from '../hooks/useGenerateInvoice';
import useInvoicePlan from '../hooks/useInvoicePlan';
import InvoiceStepBanner from './InvoiceStepBanner';
import { money } from '../utils/money';
import useCurrentUser from '../hooks/useCurrentUser';
import ProjectLeadFields from './ProjectLeadFields';
import { ProjectLeadAssignment } from '../models/project';
import { validateLeads } from '../utils/leadValidation';
import ReceivedPoAttachments from './ReceivedPoAttachments';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

interface GenerateInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  workOrderId: string;
  isSalesOrder?: boolean;
  // item 433 — the WO came from a proposal: offer the deposit-split picker
  fromProposal?: boolean;
}

// item 38: convert a work order / sales order into an invoice. The new invoice
// opens as an editable draft (Finalize locks it — slice 3).
const GenerateInvoiceModal: React.FC<GenerateInvoiceModalProps> = ({
  isOpen, onClose, workOrderId, isSalesOrder, fromProposal,
}) => {
  const { generate, isGenerating } = useGenerateInvoice();
  const { isAdmin } = useCurrentUser();
  const history = useHistory();
  const toast = useToast();
  const [itemize, setItemize] = useState(false);
  // item 433 — deposit-split override. '' = untouched → send nothing, so the
  // proposal's own DepositPercent (chosen at approval, default 50) stays in charge.
  // The backend applies it only to a proposal-driven DEPOSIT invoice.
  const [depositPercent, setDepositPercent] = useState('');
  // item 529 — the project-lead step folded INTO invoice generation (Chad): this is the
  // moment right before finalize accrues commissions, so confirm/assign the lead here.
  // Prefilled from the WO's current leads (seeded at proposal approval, or set on a
  // standalone service WO); saved back to the WO just before we generate. Admin-only,
  // matching the WorkOrder/{id}/leads gate.
  const [leads, setLeads] = useState<ProjectLeadAssignment[]>([]);
  const [savingLeads, setSavingLeads] = useState(false);
  // item 1446 (Chad) — say LOUDLY which invoice this generate produces (deposit / final / full /
  // re-bill), the % and the dollars, and whether an earlier invoice is still a Draft. Never blocks.
  const pctTyped = parseFloat(depositPercent);
  const overridePct = depositPercent !== '' && pctTyped >= 1 && pctTyped <= 100 ? pctTyped : null;
  const { plan, isLoading: planLoading } = useInvoicePlan(workOrderId, isOpen, overridePct);
  const STEP_WORD: Record<string, string> = {
    deposit: 'deposit invoice', final: 'final invoice', full: 'invoice', rebill: 're-bill invoice', backorder: 'backorder invoice',
  };
  const stepWord = plan ? STEP_WORD[plan.step] : 'invoice';

  // item 1488 (Chad) — a SERVICE invoice (WO with no proposal) itemizes by default; a
  // proposal-born PROJECT invoice starts un-itemized. Re-seeded each time the dialog opens.
  useEffect(() => {
    if (isOpen) setItemize(!fromProposal);
  }, [isOpen, fromProposal]);

  useEffect(() => {
    if (!isOpen || !workOrderId || !isAdmin) return;
    let cancelled = false;
    axios.get(`${apiUrl()}WorkOrder/${workOrderId}/leads`)
      .then((res) => {
        if (cancelled) return;
        const rows = (res.data ?? []) as { userId: string; percent: number }[];
        setLeads(rows.map((r) => ({ userId: r.userId, percent: r.percent })));
      })
      .catch(() => { if (!cancelled) setLeads([]); });
    // eslint-disable-next-line consistent-return
    return () => { cancelled = true; };
  }, [isOpen, workOrderId, isAdmin]);

  const label = isSalesOrder ? 'sales order' : 'work order';
  const { balanced, complete } = validateLeads(leads);
  const leadsValid = leads.length === 0 || (balanced && complete);

  const onGenerate = async () => {
    if (isAdmin && !leadsValid) {
      toast({ status: 'warning', title: 'Project-lead split must total 100% (or clear it).' });
      return;
    }
    const pct = parseFloat(depositPercent);
    // item 543 — the deposit split now applies to ANY work order (not just proposal-driven).
    const override = depositPercent !== '' && pct >= 1 && pct <= 100
      ? pct : null;
    try {
      // item 529 — persist the project lead(s) on the WO BEFORE generating, so the
      // invoice's finalize accrues to the right people. Skipped for non-admins (they
      // can't set leads and the endpoint would refuse them).
      if (isAdmin) {
        setSavingLeads(true);
        await axios.post(`${apiUrl()}WorkOrder/${workOrderId}/leads`, { leads });
        setSavingLeads(false);
      }
      const newId = await generate({ workOrderId, itemize, depositPercentOverride: override });
      onClose();
      // item 1446 — the toast names the step + dollars, and nags about an unfinalized earlier draft
      const stepTitle = plan
        ? `${stepWord.charAt(0).toUpperCase()}${stepWord.slice(1)}${plan.percent != null && plan.step !== 'rebill' ? ` (${plan.percent}%)` : ''} created as a Draft — ${money(plan.billsNow)}.`
        : 'Invoice generated.';
      let draftNag: string | undefined;
      if (plan && plan.drafts.length > 0) {
        draftNag = `Heads up: ${plan.drafts.map((d) => d.invoiceNumber).filter(Boolean).join(', ')} on this work order ${plan.drafts.length === 1 ? 'is' : 'are'} still a Draft — finalize that one too.`;
      } else if (plan?.step === 'deposit') {
        draftNag = `The FINAL (${money(plan.balanceAfter)}) is generated later from this work order.`;
      }
      toast({
        status: 'success', title: stepTitle, description: draftNag, duration: 9000, isClosable: true,
      });
      history.push(`/invoice/${newId}`);
    } catch (e: any) {
      setSavingLeads(false);
      const msg = e?.response?.data?.errors?.[0]
        ?? e?.response?.data
        ?? 'The invoice could not be generated.';
      toast({ status: 'error', title: 'Generate failed', description: String(msg) });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{`Generate ${stepWord}`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            {/* item 1446 — the loud step block heads the dialog */}
            <InvoiceStepBanner plan={plan} isLoading={planLoading} />
            <Text fontSize="sm" color="gray.500">
              {`Create this ${stepWord} from the ${label}. It opens as an editable draft you can finalize.`}
            </Text>
            <Checkbox isChecked={itemize} onChange={(e) => setItemize(e.target.checked)}>
              Itemize lines (group price)
            </Checkbox>
            {/* item 543 — deposit split for ANY work order. Applies to the DEPOSIT (first)
                invoice; leave blank to bill in full (proposal WOs fall back to approval terms). */}
            <Stack spacing={1}>
              <Text fontSize="sm" color="gray.500">
                {fromProposal
                  ? 'Deposit split — applies to the DEPOSIT (first) invoice; leave blank to use the terms chosen at approval.'
                  : 'Deposit split (optional) — bill a deposit now instead of the full amount; leave blank to bill in full.'}
              </Text>
              <HStack>
                {[25, 50, 100].map((p) => (
                  <Button
                    key={p}
                    size="sm"
                    variant={parseFloat(depositPercent) === p ? 'solid' : 'outline'}
                    onClick={() => setDepositPercent(depositPercent === String(p) ? '' : String(p))}
                  >
                    {`${p}%`}
                  </Button>
                ))}
                <Input
                  size="sm"
                  maxW="90px"
                  type="number"
                  min={1}
                  max={100}
                  textAlign="right"
                  placeholder={fromProposal ? 'Approval terms' : 'Full'}
                  value={depositPercent}
                  onChange={(e) => setDepositPercent(e.target.value)}
                  aria-label="Deposit percent override"
                />
                <Text fontSize="sm" color="gray.500">%</Text>
              </HStack>
            </Stack>
            {/* item 1574 (Chad) — the customer-signed hardware delivery document, uploaded right at
                conversion. Filed on the WO/SO, so it rides to the invoice + its email. Optional. */}
            <ReceivedPoAttachments docId={workOrderId} kind="delivery" mt={0} />
            {/* item 529 — assign/confirm the project lead as part of generating the invoice */}
            {isAdmin && (
              <Box>
                <Divider mb={3} />
                <Text fontSize="sm" color="gray.500" mb={2}>
                  Project-lead commission (optional) — pays only the assigned lead(s), split by %.
                  Leave empty for none.
                </Text>
                <ProjectLeadFields leads={leads} onChange={setLeads} />
              </Box>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button onClick={onGenerate} isLoading={isGenerating || savingLeads} colorScheme={plan?.step === 'final' ? 'green' : undefined}>
            {`Generate ${stepWord}`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

GenerateInvoiceModal.defaultProps = {
  isSalesOrder: false,
  fromProposal: false,
};

export default GenerateInvoiceModal;
